import { type GetError, isEmailValid } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { UpdateUserInfoRequest } from "@dealer-center/ddc-pages-configuration-interface";
import {
  USER_INFO_FIELD_NAMES,
  type UserInfoFieldName
} from "./useUserInfoEdits";

/**
 * CC9's limits for the profile write. Interstate's `maxLength` only reports and is never forwarded
 * to the `<input>`, so Save must block an over-long value itself.
 */
export const USER_INFO_MAX_LENGTHS: Record<UserInfoFieldName, number> = {
  title: 50,
  firstName: 50,
  lastName: 50,
  company: 255,
  email: 100,
  email2: 100,
  homePhone: 50,
  workPhone: 50,
  mobile: 50,
  fax: 50
};

const REQUIRED_FIELDS: readonly UserInfoFieldName[] = [
  "firstName",
  "lastName",
  "company"
];

const EMAIL_FIELDS: readonly UserInfoFieldName[] = ["email", "email2"];

export type UserInfoFieldError = "required" | "invalidEmail" | "tooLong";

export type UserInfoFieldErrors = Partial<
  Record<UserInfoFieldName, UserInfoFieldError>
>;

// `""` clears an email address, so only a non-empty value is checked.
const isInvalidEmail = (value: string | undefined) =>
  Boolean(value?.trim()) && !isEmailValid((value ?? "").trim());

/** Checked in Interstate's own rule order, so a blocked save shows the same message as the live check. */
export const validateUserInfoField = (
  field: UserInfoFieldName,
  value: string
): UserInfoFieldError | undefined => {
  if (EMAIL_FIELDS.includes(field) && isInvalidEmail(value)) {
    return "invalidEmail";
  }
  if (REQUIRED_FIELDS.includes(field) && value.trim() === "") {
    return "required";
  }
  if (value.length > USER_INFO_MAX_LENGTHS[field]) {
    return "tooLong";
  }

  return undefined;
};

/** Takes `changedFields` output, so a stored value the operator did not edit cannot block a save. */
export const validateUserInfo = (
  fields: UpdateUserInfoRequest
): UserInfoFieldErrors =>
  USER_INFO_FIELD_NAMES.reduce<UserInfoFieldErrors>((errors, field) => {
    const value = fields[field];
    const error =
      value === undefined ? undefined : validateUserInfoField(field, value);

    return error ? { ...errors, [field]: error } : errors;
  }, {});

export const firstInvalidUserInfoField = (
  errors: UserInfoFieldErrors
): UserInfoFieldName | undefined =>
  USER_INFO_FIELD_NAMES.find(field => errors[field] !== undefined);

// `tooLong` replaces Interstate's `exceedsMaxLength` text, which is English-only and echoes the value.
export const userInfoFieldErrorMessage = (
  field: UserInfoFieldName,
  error: UserInfoFieldError
): string => {
  switch (error) {
    case "required":
      return Locale.getText("FIELD_REQUIRED") as string;
    case "invalidEmail":
      return Locale.getText("UM_EMAIL_INVALID") as string;
    case "tooLong":
      return Locale.getText("UM_FIELD_TOO_LONG", {
        max: USER_INFO_MAX_LENGTHS[field]
      }) as string;
  }
};

export type UserInfoFieldMessages = Partial<Record<UserInfoFieldName, string>>;

// A `reduce` rather than `Object.fromEntries`, which lib es2017 lacks.
export const userInfoFieldErrorMessages = (
  errors: UserInfoFieldErrors
): UserInfoFieldMessages =>
  USER_INFO_FIELD_NAMES.reduce<UserInfoFieldMessages>((messages, field) => {
    const error = errors[field];

    return error
      ? { ...messages, [field]: userInfoFieldErrorMessage(field, error) }
      : messages;
  }, {});

export const emailGetError: GetError = (_label, value) => {
  const isInvalid = isInvalidEmail(value);

  return {
    triggered: isInvalid,
    validationState: isInvalid
      ? {
          isValid: false,
          message: Locale.getText("UM_EMAIL_INVALID") as string,
          messageKey: "invalidEmail",
          type: "error"
        }
      : { isValid: true, type: undefined }
  };
};

/**
 * A field's validation props: none unless the page passes `errors` (My Info does not). The
 * `hasError` mark survives the tab remount after a blocked save, which Interstate's own state does not.
 */
export const userInfoFieldValidation = (
  field: UserInfoFieldName,
  errors: UserInfoFieldMessages | undefined
) =>
  errors === undefined
    ? {}
    : {
        customErrorMessages: {
          exceedsMaxLength: Locale.getText("UM_FIELD_TOO_LONG", {
            max: USER_INFO_MAX_LENGTHS[field]
          }) as string
        },
        errorMessage: errors[field],
        getError: EMAIL_FIELDS.includes(field) ? emailGetError : undefined,
        hasError: Boolean(errors[field]),
        maxLength: USER_INFO_MAX_LENGTHS[field]
      };
